import React, { useState, useMemo } from "react";
import Swal from "sweetalert2";
import Pagination from "../Pagination";
import "./../../../assets/css/purchase/datatable.css";

export default function AppointmentTable({ appointments = [], onEdit, onDelete }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const formatThaiTimestamp = (datetime) => {
    if (!datetime) return "-";
    try {
      let str = datetime.toString().trim();
      if (str.includes(" ")) str = str.replace(" ", "T");
      str = str.split(".")[0];
      const date = new Date(str);
      if (isNaN(date.getTime())) return "-";
      const dd = date.getDate().toString().padStart(2, "0");
      const MM = (date.getMonth() + 1).toString().padStart(2, "0");
      const yyyy = date.getFullYear() + 543;
      const hh = date.getHours().toString().padStart(2, "0");
      const mm = date.getMinutes().toString().padStart(2, "0");
      return `${dd}/${MM}/${yyyy} ${hh}:${mm}`;
    } catch {
      return "-";
    }
  };

  const appointmentsWithOrder = useMemo(() => {
    const asc = [...appointments].sort((a, b) => a.Appoint_Id - b.Appoint_Id);

    return asc.map((item, index) => {
      let dt = 0;

      // Use Appoint_Time_Raw for sorting (ISO string) if available, fallback to Appoint_Time
      const timeForSorting = item.Appoint_Time_Raw || item.Appoint_Time;
      if (timeForSorting) {
        // Check if it's already formatted (contains "/") or ISO string
        if (typeof timeForSorting === 'string' && timeForSorting.includes("/")) {
          // It's formatted, try to parse it or use Appoint_Date + Time_Start
          if (item.Appoint_Date) {
            const base = new Date(item.Appoint_Date);
            let h = 0, m = 0;
            if (item.Time_Start) {
              let t = item.Time_Start;
              if (t.includes("T")) t = t.split("T")[1].substring(0, 5);
              const [hh, mm] = t.split(":");
              h = Number(hh);
              m = Number(mm);
            }
            base.setHours(h, m, 0, 0);
            dt = base.getTime();
          }
        } else {
          // It's ISO string, use directly
          dt = new Date(timeForSorting).getTime();
        }
      } else if (item.Appoint_Date) {
        const base = new Date(item.Appoint_Date);
        let h = 0, m = 0;

        if (item.Time_Start) {
          let t = item.Time_Start;
          if (t.includes("T")) t = t.split("T")[1].substring(0, 5);
          const [hh, mm] = t.split(":");
          h = Number(hh);
          m = Number(mm);
        }

        base.setHours(h, m, 0, 0);
        dt = base.getTime();
      }

      return {
        ...item,
        orderIndex: index + 1,
        sortDateTime: dt
      };
    });
  }, [appointments]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    let data = [...appointmentsWithOrder];

    if (!sortConfig.key) {
      return data.sort((a, b) => b.Appoint_Id - a.Appoint_Id);
    }

    return data.sort((a, b) => {
      let x = a[sortConfig.key];
      let y = b[sortConfig.key];

      if (sortConfig.key === "sortDateTime") {
        return sortConfig.direction === "asc" ? x - y : y - x;
      }

      if (typeof x === "number" && typeof y === "number") {
        return sortConfig.direction === "asc" ? x - y : y - x;
      }

      x = x ? x.toString() : "";
      y = y ? y.toString() : "";

      return sortConfig.direction === "asc"
        ? x.localeCompare(y, "th")
        : y.localeCompare(x, "th");
    });
  }, [appointmentsWithOrder, sortConfig]);

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column)
      return <span style={{ marginLeft: 4, color: "#fff" }}>▲▼</span>;
    return sortConfig.direction === "asc" ? (
      <span style={{ marginLeft: 4, color: "#fff" }}>▲</span>
    ) : (
      <span style={{ marginLeft: 4, color: "#fff" }}>▼</span>
    );
  };

  const handleDelete = async (item) => {
    const confirm = await Swal.fire({
      title: "คุณแน่ใจหรือไม่?",
      text: "ต้องการลบข้อมูลนัดหมายนี้หรือไม่?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E13F57",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
    });

    if (!confirm.isConfirmed) return;
    onDelete?.(item);
  };

  const totalItems = sortedData.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div
      className="card table-card border-0 rounded-0 shadow-sm px-3"
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(91vh - 320px)",
        overflow: "hidden",
      }}
    >
      <style>{`
        * { font-size: 14px !important; }
        th { cursor: pointer; user-select: none; }
        table.table { 
          border-collapse: separate !important;
          border-spacing: 0 !important;
          width: 100%; 
          min-width: 1150px; 
        }
        tbody tr:hover { background: #f5f7ff !important; }
        th, td { white-space: nowrap; }
        th span { font-size: 11px !important; }
        thead th {
          position: sticky;
          top: 0;
          z-index: 5;
          background: #598AE4;
          color: white;
        }
        .sticky-action {
          position: sticky;
          right: 0;
          background: white;
          z-index: 20;
          box-shadow: -3px 0 5px rgba(0,0,0,0.12);
        }

        thead th.sticky-action {
          background-color: #E13F57 !important;
          z-index: 21 !important;
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
        <h5 className="m-0 fw-bold">ประวัติการนัดหมายเข้าพบ</h5>
      </div>

      <div
        className="table-wrapper-scroll flex-1"
        style={{
          overflowY: "auto",
          overflowX: "auto",
          flexGrow: 1,
          padding: "0",
        }}
      >
        <table className="table table-bordered table-hover mb-0">
          <thead>
            <tr>
              <th onClick={() => handleSort("Appoint_Id")}>
                ครั้งที่ <SortIcon column="Appoint_Id" />
              </th>
              <th onClick={() => handleSort("ServiceAddr_Name")}>
                ชื่อสถานที่บริการ <SortIcon column="ServiceAddr_Name" />
              </th>
              <th onClick={() => handleSort("sortDateTime")}>
                วันที่นัดหมายเพื่อเข้าพบ <SortIcon column="sortDateTime" />
              </th>
              <th onClick={() => handleSort("Appoint_Time")}>
                เข้าพบเมื่อ <SortIcon column="Appoint_Time" />
              </th>
              <th onClick={() => handleSort("Sale_Emp_Name")}>
                พนักงานขาย <SortIcon column="Sale_Emp_Name" />
              </th>
              <th onClick={() => handleSort("SpecRemark")}>
                ผลการเข้าพบ <SortIcon column="SpecRemark" />
              </th>
              <th>สเปค</th>
              <th className="whitespace-nowrap">รายละเอียดเพิ่มเติม</th>
              <th className="sticky-action">การจัดการ</th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((a, i) => (
                <tr key={a.Appoint_Id || i}>
                  <td>{a.orderIndex}</td>

                  <td className="text-start">{a.ServiceAddr_Name || "-"}</td>

                  <td>
                    {(() => {
                      if (!a.Appoint_Date) return "-";
                      try {
                        let dateStr = a.Appoint_Date.toString().trim();
                        if (dateStr.includes(" ")) dateStr = dateStr.replace(" ", "T");
                        const [y, m, d] = dateStr.split("T")[0].split("-");
                        const dd = d.padStart(2, "0");
                        const MM = m.padStart(2, "0");
                        const yyyy = (Number(y) || 0) + 543;
                        const dateDisplay = `${dd}/${MM}/${yyyy}`;

                        const fmt = (t) => {
                          if (!t) return "";
                          if (t.includes("T")) {
                            const m = t.match(/T(\d{2}:\d{2})/);
                            return m ? m[1] : "";
                          }
                          const sp = t.split(":");
                          if (sp.length < 2) return "";
                          return `${sp[0].padStart(2, "0")}:${sp[1].padStart(2, "0")}`;
                        };

                        const t1 = fmt(a.Time_Start);
                        const t2 = fmt(a.Time_End);

                        const timeDisplay =
                          t1 && t2 ? `${t1} - ${t2}` : t1 || t2 || "";

                        return (
                          <span>
                            {timeDisplay ? `${dateDisplay} ${timeDisplay}` : dateDisplay}
                          </span>
                        );
                      } catch {
                        return "-";
                      }
                    })()}
                  </td>

                  <td>
                    {a.Appoint_Time 
                      ? (a.Appoint_Time.includes("/") 
                          ? a.Appoint_Time 
                          : formatThaiTimestamp(a.Appoint_Time))
                      : "-"}
                  </td>
                  <td>{a.Sale_Emp_Name || "-"}</td>
                  <td className="text-start">{a.SpecRemark || "-"}</td>

                  <td className="text-start">
                    {a.Width || a.Length || a.Height
                      ? `${a.Width ? a.Width + " (ม.) " : ""}${a.Length ? a.Length + " (ม.) " : ""}${a.Height ? a.Height + " (ชั้น)" : ""}`
                      : "-"}
                  </td>

                  <td className="text-start">{a.Appoint_Remark || "-"}</td>

                  <td className="sticky-action">
                    <div className="d-flex justify-content-center gap-2 flex-nowrap">
                      <button
                        onClick={() => onEdit?.(a)}
                        className="btn btn-sm text-white"
                        style={{ backgroundColor: "#598AE4" }}
                      >
                        บันทึกผล
                      </button>

                      <button
                        onClick={() => handleDelete(a)}
                        className="btn btn-sm text-white"
                        style={{ backgroundColor: "#E13F57" }}
                      >
                        ลบ
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center py-4 text-muted">
                  ไม่มีข้อมูลการเข้าพบ
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="px-2 py-2">
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(val) => {
            setItemsPerPage(val);
            setCurrentPage(1);
          }}
        />
      </div>
    </div>
  );
}
